import {
  BetaRegistrationTokenVaultActions,
  EmailActions,
  EmailAttemptStatus,
  EmailStatus,
  EmailTag,
  EmailTriggers,
  EntityDeletionReason,
  EntityStatus,
  FilePurpose,
  Genders,
  OtherEmailActions,
  PublicTokenVaultActions,
  SmsActions,
  SmsAttemptStatus,
  SmsStatus,
  SmsTag,
  SmsTriggers,
  UserEmailTokenVaultActions,
  UserRegistrationConfirmationTokenVaultActions,
  UserRegistrationStep,
  UserRole,
  UserSmsTokenVaultActions,
  UserTwoFATokenVaultActions,
} from '@workspace/data-types';
import { SendStatus } from './enum-display/send-status-display.pipe';

export class CoreNiceLabelUtils {
  static gender(gender: Genders): string {
    switch (gender) {
      case Genders.MALE:
        return 'masculin';
      case Genders.FEMALE:
        return 'féminin';
      case Genders.OTHER:
        return 'autre';
      default:
        return gender;
    }
  }

  static emailAction(action: EmailActions): string {
    switch (action) {
      case UserEmailTokenVaultActions.USER_CHANGE_EMAIL:
        return "Changement d'email";
      case UserEmailTokenVaultActions.USER_TWO_FA_CHANGE_EMAIL:
        return "2FA: Modification d'email";
      default:
        return action;
    }
  }

  static publicAction(action: EmailActions): string {
    switch (action) {
      case PublicTokenVaultActions.USER_PASSWORD_LOST:
        return 'Mot de passe oublié';
      default:
        return action;
    }
  }

  static emailTag(tag: EmailTag): string {
    switch (tag) {
      case EmailTag.CORE:
        return 'Core';
      case EmailTag.TRANSACTIONAL:
        return 'Transactionnel';
      case EmailTag.MARKETING:
        return 'Marketing';
      default:
        return tag;
    }
  }

  static smsTag(tag: SmsTag): string {
    switch (tag) {
      case SmsTag.CORE:
        return 'Core';
      case SmsTag.TRANSACTIONAL:
        return 'Transactionnel';
      case SmsTag.MARKETING:
        return 'Marketing';
      default:
        return tag;
    }
  }

  static filePurpose(tag: FilePurpose): string {
    switch (tag) {
      case FilePurpose.USER_AVATAR:
        return 'User avatar';
      default:
        return tag;
    }
  }

  static otherEmailAction(action: OtherEmailActions): string {
    switch (action) {
      case OtherEmailActions.ADMIN_TEST:
        return 'Test admin';
      case OtherEmailActions.USER_ACCOUNT_WELCOME:
        return 'Mail de bienvenue';
      case OtherEmailActions.USER_INVITATION:
        return 'Invitation utilisateur';
      case OtherEmailActions.USER_IDENTITY_VERIFIED:
        return 'Identité validée';
      case OtherEmailActions.USER_IDENTITY_REJECTED:
        return 'Identité rejetée';
      case OtherEmailActions.USER_NEW_CONNECTION:
        return 'Nouvelle connexion';
      default:
        return action;
    }
  }

  static registrationAction(action: UserRegistrationConfirmationTokenVaultActions): string {
    switch (action) {
      case UserRegistrationConfirmationTokenVaultActions.USER_REGISTRATION_EMAIL_CONFIRMATION:
        return "Inscription: Confirmation de l'email";
      case UserRegistrationConfirmationTokenVaultActions.USER_REGISTRATION_MOBILE_CONFIRMATION:
        return 'Inscription: Confirmation de mon numéro';
      default:
        return action;
    }
  }

  static betaRegistrationAction(action: BetaRegistrationTokenVaultActions): string {
    switch (action) {
      case BetaRegistrationTokenVaultActions.BETA_REGISTRATION_EMAIL_CONFIRMATION:
        return "Inscription Bêta: Confirmation de l'email";
      default:
        return action;
    }
  }

  static emailStatus(status: EmailStatus): string {
    switch (status) {
      case EmailStatus.CANCELED:
        return 'Annulé';
      case EmailStatus.PENDING:
        return 'En attente';
      case EmailStatus.ENQUEUED:
        return "En attente (dans la file d'envoi)";
      case EmailStatus.SENT:
        return 'Envoyé';
      default:
        return status;
    }
  }

  static emailAttemptStatus(status: EmailAttemptStatus): string {
    switch (status) {
      case EmailAttemptStatus.PENDING:
        return 'En attente';
      case EmailAttemptStatus.SUCCESS:
        return 'Succès';
      case EmailAttemptStatus.FAILED:
        return 'Échoué';
      case EmailAttemptStatus.CANCELED:
        return 'Annulé';
      default:
        return status;
    }
  }

  static smsAttemptStatus(status: SmsAttemptStatus): string {
    switch (status) {
      case SmsAttemptStatus.PENDING:
        return 'En attente';
      case SmsAttemptStatus.SUCCESS:
        return 'Succès';
      case SmsAttemptStatus.FAILED:
        return 'Échoué';
      case SmsAttemptStatus.CANCELED:
        return 'Annulé';
      default:
        return status;
    }
  }

  static emailTrigger(trigger: EmailTriggers): string {
    switch (trigger) {
      case EmailTriggers.DIRECT:
        return 'Direct (API)';
      case EmailTriggers.JOB:
        return 'Automatique (job)';
      case EmailTriggers.MANUAL:
        return 'Manuel (admin)';
      default:
        return trigger;
    }
  }

  static smsTrigger(trigger: SmsTriggers): string {
    switch (trigger) {
      case SmsTriggers.DIRECT:
        return 'Direct (API)';
      case SmsTriggers.JOB:
        return 'Automatique (job)';
      case SmsTriggers.MANUAL:
        return 'Manuel (admin)';
      default:
        return trigger;
    }
  }

  static smsAction(action: SmsActions): string {
    switch (action) {
      case UserSmsTokenVaultActions.USER_CHANGE_MOBILE:
        return 'Changement de numéro';
      case UserSmsTokenVaultActions.USER_TWO_FA_CHANGE_MOBILE:
        return '2FA: Modification de n° de tel.';
      default:
        return 'undefined';
    }
  }

  static twoFAAction(action: UserTwoFATokenVaultActions): string {
    switch (action) {
      case UserTwoFATokenVaultActions.DEACTIVATE_ACCOUNT:
        return 'Désactivation du compte';
      case UserTwoFATokenVaultActions.DELETE_ACCOUNT:
        return 'Suppression du compte';
      case UserTwoFATokenVaultActions.USER_TWO_FA_CHANGE_EMAIL:
        return "2FA: Modification d'email";
      case UserTwoFATokenVaultActions.USER_TWO_FA_CHANGE_MOBILE:
        return '2FA: Modification de n° de tel.';
      case UserTwoFATokenVaultActions.USER_TWO_FA_CHANGE_PASSWORD:
        return '2FA: Modification de mot de passe';
      default:
        return 'undefined';
    }
  }

  static smsStatus(status: SmsStatus): string {
    switch (status) {
      case SmsStatus.CANCELED:
        return 'Annulé';
      case SmsStatus.PENDING:
        return 'En attente';
      case SmsStatus.ENQUEUED:
        return "En attente (dans la file d'envoi)";
      case SmsStatus.PROCESSING:
        return "En cours d'envoi";
      case SmsStatus.SENT:
        return 'Envoyé';
      default:
        return status;
    }
  }

  static sendStatus(status: SendStatus): string {
    switch (status) {
      case SendStatus.CANCELED:
        return 'Annulé';
      case SendStatus.PENDING:
        return 'En attente';
      case SendStatus.SENT:
        return 'Envoyé';
      default:
        return status;
    }
  }

  static userRegistrationStep(type: UserRegistrationStep): string {
    switch (type) {
      case UserRegistrationStep.PENDING_EMAIL_VERIFICATION:
        return "Vérification d'email";
      case UserRegistrationStep.PENDING_MOBILE:
        return 'Saisi du numéro de téléphone';
      case UserRegistrationStep.PENDING_MOBILE_VERIFICATION:
        return 'Vérification du numéro de téléphone';
      case UserRegistrationStep.PENDING_PROFILE:
        return 'Complétion du profil';
      case UserRegistrationStep.PENDING_USER_CONVERTION:
        return 'Convertion utilisateur';
      case UserRegistrationStep.COMPLETED:
        return 'Terminé';
      default:
        return type;
    }
  }

  static stateStatus(type: EntityStatus): string {
    switch (type) {
      case EntityStatus.ACTIVE:
        return 'Actif';
      case EntityStatus.DRAFT:
        return 'Brouillon';
      case EntityStatus.DELETED:
        return 'Supprimé';
      case EntityStatus.DEACTIVATED:
        return 'Désactivé';
      default:
        return type;
    }
  }

  static stateReason(type: EntityDeletionReason): string {
    switch (type) {
      case EntityDeletionReason.ADMIN_ACTION:
        return 'Action admin';
      case EntityDeletionReason.SECURITY_REASON:
        return 'Sécurité';
      case EntityDeletionReason.USER_REQUEST:
        return 'Demande utilisateur';
      default:
        return type;
    }
  }

  static userRoles(type: UserRole): string {
    switch (type) {
      case UserRole.ADMIN:
        return 'Administrateur';
      case UserRole.USER:
        return 'Utilisateur';
      default:
        return type;
    }
  }

  static subscriptionStatus(status: string): string {
    switch (status) {
      case 'active':
        return 'Actif';
      case 'past_due':
        return 'Retard de paiement';
      case 'unpaid':
        return 'Impayé';
      case 'canceled':
        return 'Expiré';
      case 'incomplete':
        return 'Incomplet';
      case 'incomplete_expired':
        return 'Expiré (Incomplet)';
      case 'trialing':
        return 'Essai';
      case 'paused':
        return 'En pause';
      default:
        return status;
    }
  }

  static transactionStatus(status: string): string {
    switch (status) {
      case 'succeeded':
        return 'Réussi';
      case 'failed':
        return 'Échoué';
      case 'pending':
        return 'En attente';
      case 'requires_action':
        return 'Action requise';
      default:
        return status;
    }
  }

  static tokenVaultStatus(status: string): string {
    switch (status) {
      case 'open':
        return 'Ouvert';
      case 'used':
        return 'Utilisé';
      case 'expired':
        return 'Expiré';
      case 'canceled':
        return 'Annulé';
      default:
        return status;
    }
  }

  static tokenVaultVerification(verification: string): string {
    switch (verification) {
      case 'email':
        return 'Email';
      case 'sms':
        return 'SMS';
      default:
        return verification;
    }
  }

  static tokenVaultAction(action: string): string {
    if ((Object.values(UserRegistrationConfirmationTokenVaultActions) as string[]).includes(action)) {
      return CoreNiceLabelUtils.registrationAction(action as UserRegistrationConfirmationTokenVaultActions);
    }
    if ((Object.values(BetaRegistrationTokenVaultActions) as string[]).includes(action)) {
      return CoreNiceLabelUtils.betaRegistrationAction(action as BetaRegistrationTokenVaultActions);
    }
    if ((Object.values(UserEmailTokenVaultActions) as string[]).includes(action)) {
      return CoreNiceLabelUtils.emailAction(action as UserEmailTokenVaultActions);
    }
    if ((Object.values(UserSmsTokenVaultActions) as string[]).includes(action)) {
      return CoreNiceLabelUtils.smsAction(action as UserSmsTokenVaultActions);
    }
    if ((Object.values(UserTwoFATokenVaultActions) as string[]).includes(action)) {
      return CoreNiceLabelUtils.twoFAAction(action as UserTwoFATokenVaultActions);
    }
    if ((Object.values(PublicTokenVaultActions) as string[]).includes(action)) {
      return CoreNiceLabelUtils.publicAction(action as PublicTokenVaultActions);
    }
    return action;
  }
}
